import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MenuItem, Skeleton, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Paper, Button, Table, TableBody, TableCell, Stack, TableRow, TableHead, Card, CircularProgress, CardContent, Grid, Autocomplete, TextField, Box, Dialog } from '@mui/material';
import { ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { getMrFundReportApi, getMrFundBalanceApi, getMaintenanceEventApi, getMajorAssemblyApi, getMrCurrencyApi } from '../apiServices';
import { MRFundList } from '../components';
import useTableSortingHook from '../hooks/useTableSortingHook';
import { LeasesBasicDetails, LeasesMenuList, LeasesNav } from '../../elements';
import { ButtonLoader } from '../../../shared_elements/components';
import { NoRecordFound, PageLoader, Pagination, PopOverCTA, STableLoader, STabsLoader } from '../../../shared_elements';
import { checkApiStatus, } from '../../../utils';
import { globalGetService } from '../../../utils/globalApiServices';
import { SliderWrapper } from '../../../wrappers';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import EventBalanceModal from '../components/EventBalanceModal';
import { assetType } from "../../../constants";
import moment from 'moment';
import { trackActivity } from '../../../utils/mixpanel';
import ModuleNotExcessModal from '../../../shared_elements/components/ModuleNotExcessModal';
import BackToLeaseList from '../../elements/BackToLeaseList';
import CustomTabs from '../../../shared_elements/components/CustomTabs';

const TABLE_HEIGHT = 'calc(100vh - 480px)';
const TABLE_HEIGHT_BUFFER = 147;

const MRFundHd = ({ tableSorting, item }) => {
    return (
        <TableHead>
            <TableRow>
                <TableCell> Action(s) </TableCell>
                <TableCell> {tableSorting('period', 'Transaction Date')}</TableCell>
                <TableCell> {tableSorting('event', 'Events')}</TableCell>
                <TableCell> {tableSorting('txn_type', 'Txn Type')}</TableCell>
                <TableCell> {tableSorting('amount', 'Amount')}</TableCell>
                <TableCell> {tableSorting('balance', 'Balance')}</TableCell>
                <TableCell> {tableSorting('transaction', 'Transaction Details')}</TableCell>
            </TableRow>
        </TableHead>
    );
};


const parseMoneyValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const normalized = Number(String(value).replace(/,/g, "").replace(/[^0-9.-]/g, ""));
    return Number.isNaN(normalized) ? 0 : normalized;
};

const formatMoneyValue = (value, currency) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency?.code || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(parseMoneyValue(value));
};

const SpecialRowDetailsModal = ({ open, onClose, row }) => {
    if (!row) return null;
    const isAdditionalTax = row.row_type === "additional_tax";
    const details = isAdditionalTax ? row.additional_tax_details : row.penalty_details;
    const currency = row.currency?.code || "USD";
    const columns = details?.columns || [];
    const rows = details?.rows || [];
    const total = details?.total || {};

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: "14px", boxShadow: "0 20px 60px rgba(0,0,0,.25)", padding: "20px 22px", m: 2 } }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "2px" }}>
                <Box sx={{ fontSize: "15px", fontWeight: 600, color: "#1f1f1d" }}>{row.duration}</Box>
                <Box
                    component="button"
                    onClick={onClose}
                    sx={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#8a8a84", lineHeight: 1, p: "4px", display: "flex", alignItems: "center" }}
                >
                    ×
                </Box>
            </Box>
            <Box sx={{ fontSize: "12px", color: "#8a8a84", mb: "14px" }}>Per event type · {currency}</Box>

            {details && columns.length > 0 ? (
                <Table className="mui-table-format" size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Event</TableCell>
                            {columns.map((col, i) => (
                                <TableCell key={i} align="right">{col}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={i}>
                                <TableCell>{r.event ?? "—"}</TableCell>
                                {columns.map((col, j) => (
                                    <TableCell key={j} align="right">{r[col] ?? "—"}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Total ({currency})</TableCell>
                            {columns.map((col, i) => (
                                <TableCell key={i} align="right" sx={{ fontWeight: 700 }}>{total[col] ?? "—"}</TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            ) : (
                <Box sx={{ textAlign: "center", color: "#8a8a84", fontSize: "13px", py: 3 }}>No data available</Box>
            )}
        </Dialog>
    );
};

const BalanceCard = ({ mrBalanceInfo, isLoading, assetData, mrFundBalance, onFieldChange }) => {
    const [modalState, setModalState] = useState({ open: false, row: null });

    const openModal = (row) => setModalState({ open: true, row });
    const closeModal = () => setModalState({ open: false, row: null });

    return (
        <>
        <Box sx={{ border: "1px solid #dce3ef", borderRadius: "8px", background: "#eef2f8", padding: "6px 6px" }}>
            <Box style={{ display: isLoading?.skeletonLoader || !mrBalanceInfo?.list?.length ? "flex" : '' }} sx={{ padding: "0px 32px", '.slick-track': { display: 'flex', alignItems: 'stretch' }, '.slick-slide': { height: 'inherit', display: 'flex', flexDirection: 'column' }, '.slick-slide > div': { px: 0.5, py: 0.5, height: '100%' }, '.slick-list': { mx: -0.5 }, }}>
                {isLoading?.skeletonLoader || !assetData?.id ?
                    [...Array(4)].map((_, i) => (
                        <Stack key={i} spacing={0.5} sx={{ width: "100%" }} >
                            <Skeleton style={{ margin: '0px 0px 10px 0px' }} variant="rect" width={250} animation='wave' height={80} />
                        </Stack>
                    ))
                    :
                    <SliderWrapper key={JSON.stringify(mrBalanceInfo?.list)}>
                        {mrBalanceInfo?.list?.length && mrBalanceInfo?.list?.map((item, index) => {
                            const rowCurrency = item?.currency || item?.rows?.find(row => row?.currency)?.currency || null;
                            const scrollRows = (item?.rows || []).filter(row => {
                                if (row?.row_type === "net_balance") return false;
                                const bal = row?.balance;
                                if (bal === "—" || bal === "--" || bal === null || bal === undefined || bal === "") return false;
                                if (parseMoneyValue(bal) === 0) return false;
                                return true;
                            });
                            const netBalanceRow = (item?.rows || []).find(row => row?.row_type === "net_balance") || null;
                            const netBalance = netBalanceRow
                                ? parseMoneyValue(netBalanceRow?.balance)
                                : scrollRows.reduce((sum, row) => sum + parseMoneyValue(row?.balance), 0);

                            return (
                                <Box key={index} sx={{ height: '100%' }}>
                                    <Card sx={{ borderRadius: '10px', p: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', flexShrink: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #dce3ef' }} elevation={0}>
                                        <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>
                                            {/* Header */}
                                            <Box sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 60%, #1251a3 100%)', color: "white", fontWeight: 700, fontSize: "13px", px: 2, py: 1.1, borderTopLeftRadius: "10px", borderTopRightRadius: "10px", fontFamily: "Roboto-Regular", letterSpacing: '0.01em' }}>
                                                {item?.title}
                                            </Box>

                                            {/* Scrollable rows */}
                                            <Box sx={{ px: 2, pt: 0.5, pb: 0, flex: 1, maxHeight: '105px', overflowY: 'auto' }}>
                                                {scrollRows.map((row, rowIndex) => {
                                                    const isHighlighted = row?.is_current || row?.current || row?.active;
                                                    const currencyInfo = row?.currency || rowCurrency;
                                                    const isSpecial = row?.row_type === "additional_tax" || row?.row_type === "penalty";
                                                    const labelColor = isSpecial ? "#6a3fb5" : "#556070";
                                                    const valueColor = isSpecial ? "#6a3fb5" : (isHighlighted ? '#0e9f6e' : '#1a202c');
                                                    const displayBalance = (isSpecial && (!row?.balance || row?.balance === "—" || row?.balance === "--"))
                                                        ? "—"
                                                        : formatMoneyValue(row?.balance, currencyInfo);
                                                    const hasDetails = isSpecial && (
                                                        (row?.row_type === "additional_tax" && row?.additional_tax_details != null) ||
                                                        (row?.row_type === "penalty" && row?.penalty_details != null)
                                                    );
                                                    return (
                                                        <Box key={rowIndex} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.7, borderBottom: "1px solid #eff0f2", fontSize: "13px", color: "#4a5568" }}>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                                <Box component="span" sx={{ fontWeight: 500, color: labelColor }}>{row?.duration || "--"}</Box>
                                                                {hasDetails && (
                                                                    <Box
                                                                        component="span"
                                                                        onClick={() => openModal(row)}
                                                                        sx={{ fontSize: "10px", color: "#2f6fdb", cursor: "pointer", ml: "4px", textDecoration: "underline", '&:hover': { color: "#1a56b0" } }}
                                                                    >
                                                                        view
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                                <Box sx={{ fontWeight: 600, color: valueColor }}>
                                                                    {displayBalance}
                                                                </Box>
                                                                {row?.excess_amount > 0 && (
                                                                    <Tooltip title={`Excess Amount: ${formatMoneyValue(row?.excess_amount, currencyInfo)}`} arrow>
                                                                        <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b', cursor: 'pointer' }} />
                                                                    </Tooltip>
                                                                )}
                                                                {isHighlighted && !isSpecial && (
                                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#0e9f6e', flexShrink: 0 }} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>

                                            {/* Net Balance */}
                                            <Box sx={{ px: 2, pt: 1, mt: 0.5, borderTop: "1.5px solid #c8d0df", fontWeight: 700, fontSize: "14px", pb: 1.2, color: '#1a202c', display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Box>Net Balance</Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                    <Box>{formatMoneyValue(netBalance, netBalanceRow?.currency || rowCurrency)}</Box>
                                                    {netBalanceRow?.excess_amount > 0 && (
                                                        <Tooltip title={`Excess Amount: ${formatMoneyValue(netBalanceRow?.excess_amount, netBalanceRow?.currency || rowCurrency)}`} arrow>
                                                            <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b', cursor: 'pointer' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            );
                        })}
                    </SliderWrapper>}
            </Box>
            {assetData?.id && !isLoading?.skeletonLoader && !mrBalanceInfo?.list?.length && <p style={{ textAlign: 'center', fontFamily: "Roboto-Regular", fontSize: '13px', fontWeight: '500' }}>No records found</p>}
        </Box>
        <SpecialRowDetailsModal open={modalState.open} onClose={closeModal} row={modalState.row} />
        </>
    )
}

const MRFundReport = () => {
    const location=useLocation();
    const navigate = useNavigate()
    const params = useParams();
    const { leaseBasicDetails } = useSelector((state) => state.leases);
    const assetData = leaseBasicDetails?.asset
    const [isLoading, setLoading] = useState({ skeletonLoader: false, pageLoader: false, buttonLoader: false });
    const toggleLoader = (type, status) => { setLoading({ [type]: status }) }
    const [mrFundReports, setMrFundReports] = useState({})
    const [mrReportsInfo, setMrReportsInfo] = useState({ list: [], pagination: {}, });
    const [mrBalanceInfo, setMrBalanceInfo] = useState({ list: [] });
    const [eventBalance, setEventBalance] = useState({ mode: '', modal: false, data: null });
    const [isExport, seIsExport] = useState(false)
    const [pltConstants, setPltConstants] = useState({ asset_types: [] });
    const [dropDownLoader, setDropDownLoader] = useState({ major_assembly: false, maintenance_events: false, mr_currency:false })
    const [queryConfig, setQueryConfig] = useState({ page: mrReportsInfo?.pagination?.page || 1, per_page: mrReportsInfo?.pagination?.per_page || 20, });
    const [mrFundBalance, setMrFundBalance] = useState({ as_of_date: moment(new Date()).format(backendDateFormat) })
    const [mrCurrencyList, setMrCurrencyList] = useState([]);
    const [currency, setcurrency] = useState(null);
    const [tabIndex, setTabIndex] = useState(location?.pathname?.includes('balance') ? 0 : 1);
    const topSectionRef = useRef(null);
    const [topSectionHeight, setTopSectionHeight] = useState(0);

    useEffect(() => {
        const node = topSectionRef.current;
        if (!node) return;
        const observer = new ResizeObserver((entries) => {
            const height = entries?.[0]?.contentRect?.height;
            if (typeof height === 'number') setTopSectionHeight(height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    
    useEffect(() => {
        trackActivity('Page Viewed', { page_title: 'MR Balance' })
        getMrCurrencyList();
    }, []);

    useEffect(() => {
        setTabIndex(location?.pathname?.includes('balance') ? 0 : 1);
    }, [location.pathname]);

    useEffect(() => {
        if (!leaseBasicDetails) return;
        if (leaseBasicDetails?.status === "Draft") return;
        getMajorAssembly();
    }, [leaseBasicDetails?.status]);

    useEffect(() => {
        if (!leaseBasicDetails) return;
        if (leaseBasicDetails?.status === "Draft") return;
        if (assetData?.id && currency) {
            getMrFundBalance('skeletonLoader')
        }
        if (assetData?.id && assetData?.asset_type) {
            getMaintenanceEvents(assetData)
        }
    }, [assetData,tabIndex,currency]);

    const leaseStartDate = leaseBasicDetails?.start_date ? moment(leaseBasicDetails.start_date) : null;
    const leaseEndDate = leaseBasicDetails?.end_date ? moment(leaseBasicDetails.end_date) : null;

    const isDateWithinLeasePeriod = (date) => {
        if (!moment(date).isValid()) return false;
        const d = moment(date);
        if (leaseStartDate && d.isBefore(leaseStartDate, 'day')) return false;
        if (leaseEndDate && d.isAfter(leaseEndDate, 'day')) return false;
        return true;
    };

    const onMrBalanceFieldChange = (key, value) => {
        if (!moment(value).isValid() || !isDateWithinLeasePeriod(value)) {
            return;
        }
        setMrFundBalance({ ...mrFundBalance, [key]: value });
        getMrFundBalance("pageloader", value);
        onFieldChange('', '', value);
    }

    const onFieldChange = (key, value, asOfdate) => {
        const currentPerPage = mrReportsInfo?.pagination?.per_page || 20;
        if(asOfdate){
            setMrFundReports({ ...mrFundReports, ['major_assembly']: pltConstants?.asset_types && pltConstants?.asset_types?.filter((item) => item?.id === assetData?.id)[0], ['maintenance_check']: null });
        }
        if (key === "major_assembly") {
            if (!value) {
                setMrFundReports({ ...mrFundReports, ['maintenance_check']: null });
                return getMrFundReportList({ page: 1, per_page: currentPerPage }, 'pageloader', value)
            }
            setMrFundReports({ ...mrFundReports, ['major_assembly']: value, ['maintenance_check']: null });
            if (value?.id && value?.asset_type) {
                getMaintenanceEvents(value)
                getMrFundReportList({ page: 1, per_page: currentPerPage }, 'pageloader', "", value)
            }
        }
        if (key === "maintenance_check") {
            if (!value) {
                setMrFundReports({ ...mrFundReports, ['maintenance_check']: value });
                getMrFundReportList({ page: 1, per_page: currentPerPage }, 'pageloader', value, mrFundReports?.major_assembly)
            } else {
                setMrFundReports({ ...mrFundReports, ['maintenance_check']: value });
                getMrFundReportList({ page: 1, per_page: currentPerPage }, 'pageloader', value, mrFundReports?.major_assembly)
            }
        }
    }

    const handleCurrencyChange = (newMode) => {
		setcurrency(newMode);
	}

    const getMrFundReportList = (query = {}, loaderType, event,asset,asOfDate) => {
       loaderType === 'skeletonLoader' ?  toggleLoader("skeletonLoader", true) :  toggleLoader("pageLoader", true);
       if(tabIndex === 1) query={...query,report_all:true}
       if(!query?.sort && !query?.sort_by) setQueryConfig({ page: mrReportsInfo?.pagination?.page || 1, per_page: mrReportsInfo?.pagination?.per_page || 20, })
        globalGetService(getMrFundReportApi({ as_of_date:asOfDate? asOfDate : mrFundBalance?.as_of_date, lease_slug: params?.leaseSlug, event: event?.id, 
             asset_id: asset?.id ? asset?.id :leaseBasicDetails?.asset?.id,
            asset_type: asset?.asset_type ? asset?.asset_type : leaseBasicDetails?.asset?.asset_type}, { ...query, currency: currency }), {}).then(response => {
            if (checkApiStatus(response)) {
                loaderType === 'skeletonLoader' ?  toggleLoader("skeletonLoader", false) :  toggleLoader("pageLoader", false);
                setMrReportsInfo(response?.data?.data || []);
            } else {
                loaderType === 'skeletonLoader' ?  toggleLoader("skeletonLoader", false) :  toggleLoader("pageLoader", false);
            }
        });
    };
    const getMrFundBalance = (loaderType, value) => {
        loaderType === "skeletonLoader" ? toggleLoader("skeletonLoader", true) : toggleLoader("pageLoader", true)
        let query = {};
        if (tabIndex === 1) query = { ...query, report_all: true }
        globalGetService(getMrFundBalanceApi({ as_of_date: value ? value : mrFundBalance?.as_of_date, lease_slug: params?.leaseSlug }, { ...query, currency: currency })).then(response => {
            if (checkApiStatus(response)) {
                loaderType === "skeletonLoader" ? toggleLoader("skeletonLoader", false) : toggleLoader("pageLoader", false)
                getMrFundReportList({ per_page: 20 }, 'skeletonLoader', "","",value)
                setMrBalanceInfo(response?.data?.data || []);
            } else {
                loaderType === 'skeletonLoader' ?  toggleLoader("skeletonLoader", false) :  toggleLoader("pageLoader", false);
            }
        });
    };
    const getMajorAssembly = () => {
        setDropDownLoader(prev => ({ ...prev, major_assembly: true }));
        globalGetService(getMajorAssemblyApi(params))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, major_assembly: false }));
                if (checkApiStatus(response)) {
                    setPltConstants(prevConstants => ({ ...prevConstants, asset_types: response?.data?.data || [] }));
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
                }
            })
    }
    const getMaintenanceEvents = (leaseData) => {
        setDropDownLoader(prev => ({ ...prev, maintenance_events: true }));
        globalGetService(
            getMaintenanceEventApi(params, leaseData?.asset_type, leaseData?.id))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, maintenance_events: false }));
                if (checkApiStatus(response)) {
                    const updatedMaintenanceEvents = response?.data?.data[0]?.maintenance_events?.length > 0 ? response?.data?.data[0]?.maintenance_events : response?.data?.data;
                    setPltConstants(prevConstants => ({ ...prevConstants, maintenance_events: updatedMaintenanceEvents || [], }));
                }
            })
    }
    const getMrCurrencyList = () => {
        setDropDownLoader(prev => ({ ...prev, mr_currency: true }));
        globalGetService(getMrCurrencyApi({ lease_slug: params?.leaseSlug, invoice_type: 1 }))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, mr_currency: false }));
                if (checkApiStatus(response)) {
                    const Currencies=response?.data?.data?.currency?.length ? response?.data?.data?.currency : [{ id: 1, code: 'USD' }];
                    setMrCurrencyList(Currencies);
                    setcurrency(response?.data?.data?.currency[0]?.code || 'USD')
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
                }
            })
    }

    const createSortHandler = (key) => {
        const isAsc = queryConfig.sort_by === key && queryConfig.sort === 'asc';
        const updatedSort = isAsc ? 'desc' : 'asc';
        setQueryConfig({ page: mrReportsInfo?.pagination?.page || 1, per_page: mrReportsInfo?.pagination?.per_page || 20, sort_by: key, sort: updatedSort });
        getMrFundReportList({ page: mrReportsInfo?.pagination?.page || 1, per_page: mrReportsInfo?.pagination?.per_page || 20, sort: updatedSort, sort_by: key, }, 'pageLoader', mrFundReports?.maintenance_check);
    };

    const { tableSorting } = useTableSortingHook(queryConfig, createSortHandler)

    const handleExport = () => {
        seIsExport(true);
        globalGetService(getMrFundReportApi({...assetData,asset_id :assetData?.id ||assetData?.asset_id,lease_slug :params?.leaseSlug ,as_of_date : mrFundBalance?.as_of_date,event: mrFundReports?.maintenance_check}, { export: true })).then((response) => {
            if (checkApiStatus(response)) {
                enqueueSnackbar("MR Balance export sent successfully", { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
                seIsExport(false);
            }
        })
    }

    const onTabChange=(val)=>{
        const tabName=val===0?'balance':'receivable'
        navigate(`/lease/${params?.leaseSlug}/mr-report-${tabName}`)
        setTabIndex(val);
        setQueryConfig({ page: 1, per_page: 20 });
    }

    const handleItemClicked = (page_type) => {
    trackActivity("Item Clicked", { page_title: `MR Balance`, page_type: page_type });
    };
    return (
        <> <LeasesBasicDetails />
            <Paper className='lease-details-section lease-nav-module-margin lease-module-paper' elevation={2}>
                <LeasesNav />
                {leaseBasicDetails?.status === "Draft" ? (<ModuleNotExcessModal moduleName="MR Balance" />) : leaseBasicDetails && Object.keys(leaseBasicDetails).length > 0 && !leaseBasicDetails?.status ? (<ModuleNotExcessModal leaseNotFound />) :
                <div className='lease-details-content'>
                    <div ref={topSectionRef}>
                    <Paper   square className="responsive-paper">
                        <Grid container spacing={1} className="responsive-grid" alignItems="end">
                                <Grid item size={{ md: 4, xs: 12 }} className="menu-container" sx={{ paddingBottom: '6px' }}>
                                <BackToLeaseList />
                            </Grid>
                            <Grid item size={{ md: 8, xs: 12 }}>
                                <ul className='list-inline lease-details-right-items lease-details-btn' style={{ display: 'flex', alignItems: 'center',padding:0 }}>
                                    <li className='lease-mobile-button lease-list-link-mt'>
                                        { !assetData?.id || dropDownLoader?.mr_currency ? <Skeleton variant="rect" width={200} animation='wave' height={33} /> :
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    label="Balance As of Date"
                                                    value={mrFundBalance?.as_of_date ? moment(mrFundBalance.as_of_date) : null}
                                                    minDate={leaseStartDate || undefined}
                                                    maxDate={leaseEndDate ? moment.min(leaseEndDate, moment()) : moment()}
                                                    onChange={(data, value) => onMrBalanceFieldChange('as_of_date', moment(data).format(backendDateFormat))}
                                                    slotProps={{
                                                        textField: {
                                                            InputLabelProps: { shrink: true },
                                                            size: "small",
                                                            margin: "none",
                                                            helperText: '',
                                                            error: false,
                                                        },
                                                    }}
                                                    sx={{ width: "100%", '& .MuiInputBase-root': { height: 33, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.89rem' } }}
                                                    format={fieldDateFormat}
                                                    />
                                                </LocalizationProvider>
                                            }
                                        </li>
                                        <li className="list-inline-item lease-mobile-button lease-list-link-mt">
                                            {!assetData?.id || dropDownLoader?.mr_currency ? <Skeleton variant="rect" width={60} animation='wave' height={33} /> : <>
                                                {mrCurrencyList?.length > 1 ?
                                                    <PopOverCTA btnText={currency} onCloseItem={true}>
                                                        <>
                                                            {mrCurrencyList.map((currency) => (
                                                                <MenuItem key={currency?.id} onClick={() => { handleCurrencyChange(currency?.code || ""); handleItemClicked(`Clicked on ${currency?.code || ""}`); }} >{currency?.code || "--"}</MenuItem>
                                                            ))}
                                                        </>
                                                    </PopOverCTA> : null}
                                            </>}
                                        </li>
                                    {/* <li className='lease-mobile-button' sx={{mt:1}}> <Button startIcon={isExport ? <ButtonLoader size={15} /> : <ArrowDownwardIcon />} onClick={() => { handleExport(); handleItemClicked("Export Button") }} variant="outlined" size='small' className='btn-primary'> Export </Button>  </li> */}
                                </ul>
                            </Grid>
                                <Grid item size={{ md: 12 }} sx={{ display: { xs: "none", sm: "block" } }}>
                                    <CustomTabs
                                        tabsList={['Balance', 'Receivable']}
                                        onChange={(e, val) => { onTabChange(val); setMrFundReports({}); }}
                                        value={tabIndex}
                                    />
                                </Grid>
                        </Grid>
                    </Paper>
                    <BalanceCard mrFundBalance={mrFundBalance} onFieldChange={onMrBalanceFieldChange} assetData={assetData} mrBalanceInfo={mrBalanceInfo} isLoading={isLoading} />
                    <Grid container spacing={1} sx={{ margin: "15px 0px 5px 0px" }}>
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                clearIcon={false}
                                options={pltConstants?.asset_types || []}
                                isOptionEqualToValue={(option, value) => option?.id === value?.asset_id}
                                disabled={dropDownLoader?.major_assembly}
                                getOptionLabel={(option) => `${option?.asset_name || ''} ${option?.name || ''}` || ""}
                                noOptionsText={dropDownLoader?.major_assembly ? "loading" : "No Option Available"}
                                value={mrFundReports?.major_assembly || pltConstants?.asset_types && pltConstants?.asset_types?.filter((item) => item?.id === assetData?.id)[0] || null}
                                onChange={(e, value) => onFieldChange("major_assembly", value)}
                                renderInput={(params) => {
                                    const selectedLabel = params?.inputProps?.value || "";
                                    return (
                                    <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
                                    <TextField
                                        required
                                        {...params}
                                        label="Major Assembly"
                                        margin='normal'
                                        size='small'
                                        disabled={dropDownLoader?.major_assembly}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            input: {
                                                ...params?.InputProps, endAdornment: (<>{dropDownLoader?.major_assembly ? (<CircularProgress color="inherit" size={20} />) : null}
                                                    {params?.InputProps?.endAdornment}
                                                </>
                                                )
                                            }
                                        }}
                                    />
                                </Tooltip>)}}
                            />
                        </Grid>
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                options={pltConstants?.maintenance_events || []}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                disabled={dropDownLoader?.maintenance_check}
                                getOptionLabel={(option) => `${option?.asset_name || ''} ${option?.name || ''}` || ""}
                                noOptionsText={dropDownLoader?.major_assembly ? "loading" : "No Option Available"}
                                value={mrFundReports?.maintenance_check || null}
                                onChange={(e, value) => onFieldChange("maintenance_check", value)}
                                renderInput={(params) => {
                                    const selectedLabel = params?.inputProps?.value || "";
                                    return (
                                    <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow > 
                                    <TextField
                                        // required
                                        {...params}
                                        label="Events List"
                                        margin='normal'
                                        size='small'
                                        disabled={dropDownLoader?.maintenance_check}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            input: {
                                                ...params?.InputProps, endAdornment: (<>{dropDownLoader?.maintenance_check ? (<CircularProgress color="inherit" size={20} />) : null}
                                                    {params?.InputProps?.endAdornment}
                                                </>
                                                )
                                            }
                                        }}
                                    />
                                </Tooltip>)}}
                            />
                        </Grid>
                    </Grid>
                    </div>
                        <Paper className='lease-detail-table lease-module-paper' elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: topSectionHeight ? `calc(100vh - ${topSectionHeight + TABLE_HEIGHT_BUFFER}px)` : TABLE_HEIGHT }}>
                            <div style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                                {isLoading?.skeletonLoader ? <STableLoader count={4} rowCount={7} /> : (
                                    <Table className='mui-table-format' stickyHeader>
                                        <MRFundHd item={mrReportsInfo?.list} tableSorting={tableSorting} />
                                        <TableBody>
                                            {mrReportsInfo?.list.length > 0 && mrReportsInfo?.list?.map((item, index) => {
                                                return (
                                                    <MRFundList
                                                        key={index}
                                                        item={item}
                                                        params={params}
                                                        viewEventBalance={() => setEventBalance({ modal: true, data: item, mode: 'view' })}
                                                        onView={() => { window.open(item?.action, "_blank"); handleItemClicked("ViewIcon"); }}
                                                    />
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                                {!isLoading?.skeletonLoader && !mrReportsInfo?.list?.length && <NoRecordFound />}
                            </div>
                            <Paper>
                                <Pagination
                                    pagination={mrReportsInfo?.pagination}
                                    onPageChange={(event, newPage) => getMrFundReportList({ page: newPage + 1, per_page: mrReportsInfo?.pagination?.per_page }, 'pageLoader', mrFundReports?.maintenance_check, mrFundReports?.major_assembly)}
                                    onRowsPerPageChange={(e) => getMrFundReportList({ page: 1, per_page: e.target.value }, 'pageLoader', mrFundReports?.maintenance_check, mrFundReports?.major_assembly)}
                                />
                            </Paper>
                        </Paper>
                    </div>}
            </Paper>
            {eventBalance?.modal &&
                <EventBalanceModal
                    moduleName={"mr_fund_report"}
                    eventBalance={eventBalance}
                    onToggleModal={() => setEventBalance({ modal: false, mode: '', data: null })}
                />
            }
            {isLoading?.pageLoader ? <PageLoader /> : null}
        </>

    )
}
export default MRFundReport;
